import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User } from '../../types/User';
import { createAppAsyncThunk } from '../../common/thunk';
import UserService, { UpdateUserRequestPayload } from '../../services/user.service';
import DashboardService from '../../services/dashboard.service';
import { IAppointmentType } from '../../common/interfaces/ServicesInterfaces';
import SectorExternal from '../../common/interfaces/SectorExternal';
import currencies from '../../assets/currencies.json';

export const getUser = createAppAsyncThunk('users/id', async (id: string) => {
  return await UserService.getUser(id).then(response => response.data);
});

export const deleteMe = createAppAsyncThunk('users/delete', async (password: string) => {
  return await UserService.deleteMe(password).then(response => response.data);
});

export const getMe = createAppAsyncThunk('users/me', async () => {
  return await UserService.getMe().then(response => response.data);
});

export const getProfessionalStatus = createAppAsyncThunk('users/professional', async () => {
  return await UserService.getMe().then(response => response.data);
});

export const getPaymentStatus = createAppAsyncThunk('user/payment/status', async () => {
  return await UserService.getMe().then(response => response.data);
});

export const updateUser = createAppAsyncThunk('user/update', async (data: UpdateUserRequestPayload) => {
  return await UserService.updateUser(data).then(response => response.data);
});

export const getAppConfiguration = createAppAsyncThunk('app/configuration', () => {
  return DashboardService.getFilters().then(response => response.data);
});

export const getSectors = createAppAsyncThunk('onboarding/getSectors', () => {
  return DashboardService.getSectors().then(response => response.data);
});

interface AppState {
  appConfigurationLoaded: boolean;
  paymentFailedError: string;
  isProfessionalStatusFetched: boolean;
  userInfo?: User;
  username: string;
  languages: string[];
  paymentMethods: string[];
  distances: number[];
  appointments: IAppointmentType[];
  audiences: string[];
  currencies: string[];
  sectors: SectorExternal[];
  domain: string;
}

const initialState: AppState = {
  appConfigurationLoaded: false,
  paymentFailedError: '',
  isProfessionalStatusFetched: false,
  username: '',
  languages: ['English'],
  paymentMethods: [],
  distances: [],
  appointments: [],
  audiences: [],
  currencies: ['USD', 'Euro'],
  sectors: [],
  domain: '',
};

const setUserInfo = (state: AppState, action: PayloadAction<User>) => {
  const userInfo = action.payload;
  state.userInfo = userInfo;
  state.username = (userInfo?.firstName || '') + ' ' + (userInfo?.lastName || '');
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setAppDomain: (state, action) => {
      state.domain = action.payload;
    },
    clearUserInfo: state => {
      state.userInfo = undefined;
    },
    clearAppData: () => {
      return initialState;
    },
  },
  extraReducers: builder => {
    builder.addCase(updateUser.fulfilled, setUserInfo);
    builder.addCase(getUser.fulfilled, setUserInfo);
    builder.addCase(getMe.fulfilled, setUserInfo);
    builder.addCase(getMe.rejected, state => {
      state.username = '';
      state.userInfo = undefined;
    });
    builder.addCase(getPaymentStatus.pending, state => {
      state.paymentFailedError = '';
    });
    builder.addCase(getPaymentStatus.fulfilled, (state, action: PayloadAction<User>) => {
      if (action.payload?.payment?.status !== 'succeeded') {
        state.paymentFailedError = 'common.payment.failed.messages';
      }
    });
    builder.addCase(getProfessionalStatus.rejected, state => {
      state.isProfessionalStatusFetched = true;
    });
    builder.addCase(getProfessionalStatus.pending, state => {
      state.isProfessionalStatusFetched = false;
    });
    builder.addCase(getProfessionalStatus.fulfilled, (state, action: PayloadAction<User>) => {
      state.isProfessionalStatusFetched = true;
      if (action.payload?.professionalId) {
        setUserInfo(state, action);
      }
    });
    builder.addCase(getAppConfiguration.fulfilled, (state, action) => {
      state.languages = action.payload.language;
      state.paymentMethods = action.payload.paymentMethods;
      state.distances = action.payload.distance || [];
      state.appointments = action.payload.appointmentType || [];
      state.audiences = action.payload.targetAudience || [];
      state.currencies = Object.keys(currencies);
      state.appConfigurationLoaded = true;
    });
    builder.addCase(getSectors.fulfilled, (state, action) => {
      state.sectors = action.payload;
    });
  },
});

export const { setAppDomain, clearUserInfo, clearAppData } = appSlice.actions;

export default appSlice.reducer;
